import React from 'react';
import {
  Card,
  Avatar,
  Typography,
  Grid,
  Box,
  Chip
} from '@mui/material';

export type MatchedUser = {
  matchedUserId: string;
  firstName: string;
  profilePhotoId: string | null;
  lastMessage: string | null;
  lastMessageDateTime: string | null;
  lastMessageRead?: boolean;
  isTyping?: boolean;
  isLastMessageFromMe?: boolean; // Yeni eklenen prop
};

type Props = {
  users: MatchedUser[];
  baseURL: string;
  onChatStart: (matchedUser: MatchedUser) => void;
};

const formatLastMessageDate = (dateStr: string | null): string | null => {
  if (!dateStr) return null;

  const date = new Date(dateStr);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Şimdi';
  if (diffMinutes < 60) return `${diffMinutes} dakika önce`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} saat önce`;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

const MatchedUsersList: React.FC<Props> = ({ users, baseURL, onChatStart }) => {
  return (
    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 12, sm: 12, md: 12 }}>
      {users.map((user) => {
        // Sadece karşıdan gelen ve okunmamış mesajlar için bold stili uygula
        const shouldBold = user.isLastMessageFromMe === false && user.lastMessageRead === false;

        return (
          <Grid
            key={user.matchedUserId}
            size={{ xs: 12, sm: 12, md: 12 }}  // 12 sütunluk gridde; xs tam satır, sm yarı, md 1/3
          >
            <Card
              sx={{
                borderRadius: 2,
                padding: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
              onClick={() => onChatStart(user)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onChatStart(user);
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Avatar
                  src={
                    user.profilePhotoId
                      ? `${baseURL}/object-store/img/get/${user.matchedUserId}/THUMB/${user.profilePhotoId}`
                      : '/placeholder.png'
                  }
                  alt={user.firstName}
                  sx={{ width: 64, height: 64 }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="h6"
                      noWrap
                      sx={{
                        fontWeight: shouldBold ? 'bold' : 'normal',
                        color: shouldBold ? 'text.primary' : 'inherit'
                      }}
                    >
                      {user.firstName || 'Kullanıcı'}
                    </Typography>
                    {user.lastMessageDateTime && (
                      <Typography variant="caption" color="text.disabled">
                        {formatLastMessageDate(user.lastMessageDateTime)}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user.isTyping ? (
                      <Chip
                        label="Typing..."
                        size="small"
                        color="primary"
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          pr: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: shouldBold ? 'bold' : 'normal'
                        }}
                      >
                        {user.lastMessage ?? 'Henüz mesaj yok'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MatchedUsersList;